/**
 * EventsAwaitingSourceCTA — migration 1360. A small, collapsible list on the Events page of upcoming gun shows and
 * range nights whose event CTA is not ready: waiting for the promoter's / host's CTA with tracking codes (with the
 * reason), or prepared in the background with the QR code and flyer still to be drawn (they are drawn when someone
 * who can edit the event opens its Call to Action tab). Renders nothing when the list is empty or fails to load.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import {
  describeAwaitingReason,
  listEventsAwaitingSourceCTA,
  type EventAwaitingSourceCTA,
} from "../../services/eventCTAInit.service";

const TYPE_LABEL: Record<string, string> = {
  gun_show: "Gun show",
  range_night: "Range night",
};

const EventsAwaitingSourceCTA: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["events-awaiting-source-cta"],
    queryFn: listEventsAwaitingSourceCTA,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const rows: EventAwaitingSourceCTA[] = data ?? [];
  if (rows.length === 0) {
    return null;
  }
  const waiting = rows.filter((r) => r.status === "awaiting_source").length;
  const manual = rows.filter((r) => r.status === "manual_only").length;
  const qrPending = rows.filter((r) => r.status === "qr_pending").length;
  const parts = [
    waiting > 0 &&
      `${waiting} upcoming gun show${waiting === 1 ? "" : "s"} / range night${waiting === 1 ? "" : "s"} waiting for a promoter or host CTA`,
    manual > 0 && `${manual} to prepare by hand (QR code already made)`,
    qrPending > 0 && `${qrPending} prepared, QR code pending`,
  ].filter(Boolean);

  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50"
      data-testid="events-awaiting-source-cta"
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-amber-900"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
        <span className="flex-1">Event CTAs: {parts.join(" · ")}</span>
        {open ? (
          <ChevronDownIcon className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 shrink-0" />
        )}
      </button>
      {open && (
        <div className="border-t border-amber-200 px-4 py-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-amber-900">
                <th className="py-1 pr-3">Event</th>
                <th className="py-1 pr-3">Date</th>
                <th className="py-1 pr-3">State</th>
                <th className="py-1">Why</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.event_id} className="border-t border-amber-100">
                  <td className="py-1 pr-3">
                    <Link
                      to={`/events/${r.event_id}?tab=cta`}
                      className="text-primary-700 hover:underline"
                    >
                      {r.title}
                    </Link>
                    <span className="ml-1 text-xs text-gray-500">
                      {TYPE_LABEL[r.type] ?? r.type}
                    </span>
                  </td>
                  <td className="py-1 pr-3 whitespace-nowrap">
                    {new Date(r.start_time).toLocaleDateString()}
                  </td>
                  <td className="py-1 pr-3">{r.state ?? "—"}</td>
                  <td className="py-1 text-gray-700">
                    {describeAwaitingReason(r)}
                    {r.status === "awaiting_source" &&
                      r.source_organization_id && (
                        <>
                          {" — "}
                          <Link
                            to={`/organizations/${r.source_organization_id}`}
                            className="text-primary-700 hover:underline"
                          >
                            open {r.source_organization_name ?? "organization"}
                          </Link>
                        </>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EventsAwaitingSourceCTA;
